import {Service, service, inject} from "./di";
import {Backflipp} from "./BackflippService";
import {LocationService} from "./LocationService";
import {BlacklistService} from "./BlacklistService";
import {DateTimeService} from "./DateTimeService";
import {DataJSON} from "../definitions/BackflippDefinitions";
import {Flyer, FlyerDetails, FlyerItemDetails, FlyerCategory} from "../definitions/FlyerDefinitions";
import {Category} from "../definitions/CommonDefinitions";
import * as array from "../utils/array";
import {i18n} from "../i18n/I18n";
import {ConfigService, RuntimeConfig} from "./ConfigService";
import {slugify} from "../utils/slugify";

@service
export class FlyerService extends Service {

	@inject locationService: LocationService;
	@inject backflipp: Backflipp;
	@inject blacklist: BlacklistService;
	@inject config: ConfigService;
	@inject dateTimeService: DateTimeService;

	_cachedFlyers: any = [];

	private _flyers: Promise<Flyer[]>;
	/* Cached Flyers. This can be used by components in cases were we don't want to consume
	 * a promise. For example, in components which adopted the renderLoading() API, calling
	 * getFlyers() from prepare() will always call renderLoading() even if the promise is
	 * resolved. This will lead to flickering UI. As a workaround, expose the raw cached
	 * flyers which can be used if available, or by calling getFlyers() if not available */
	private _flyersDataJSONCache:  {[key: string]: Promise<DataJSON>} = {};
	private _flyersCache:  {[key: string]: Promise<Flyer[]>} = {};
	private _flyerDetailsCache: {[key: number]: Promise<FlyerDetails>} = {};
	private _itemCache: { [itemId: number]: Promise<FlyerItemDetails> } = {};
	private _categoryResultsCache: Category[];

	setCachedFlyers(flyers: Flyer[]) {
		this._cachedFlyers = flyers;
	}

	async getFlyer(id: number, isFullFlyer: boolean = true, getFromCache: boolean = false): Promise<Flyer> {
		if (this._cachedFlyers.length !== 0 && getFromCache) {
			const flyers = this._cachedFlyers.filter((flyer: any) => flyer.id === id);

			const flyer = flyers.length === 0 ? null : flyers[0];
			if (flyer) {
				return flyer;
			}
		}

		if (isFullFlyer && this.config.aidHero) {
			const { flyer } = await this.getFullFlyer(id);
			return flyer;
		}

		const flyers = await this.getFlyers();
		const flyersById = flyers.filter(flyer => flyer.id === id);
		return flyersById.length === 0 ? null : flyersById[0];
	}

	getFlyerItem(id: number): Promise<FlyerItemDetails> {
		if (!this._itemCache[id]) {
			this._itemCache[id] =
					this.backflipp.getFlyerItem(String(id), this.locationService.postalCode)
						.then((item) => {
							// If the item does not have validity dates, get it from the flyer
							// FIXME [FW-158] enforce this in backflipp.
							// NOTE: 'valid_to' and 'valid_from' are strings. They need to be represented in
							// the ISO format e.g. "2017-11-23T00:00:00-05:00" (via momentjs)
							// as opposed to "Thu Nov 23 2017 00:00:00 GMT-0500 (EST)" (via Date.toString())
							// because they will need to be normalized later (inside FlyerItemElement via normalizeDate()),
							// which expects them to be in the ISO format.
							if (item && (!item.item.valid_from || !item.item.valid_to)) {
								return this.getFlyer(item.item.flyer_id)
										.then((flyer) => {
											if (!flyer) return null;
											if (!item.item.valid_from) {
												item.item.valid_from = flyer.validFrom;
											}
											if (!item.item.valid_to)
												item.item.valid_to = flyer.validTo;
											return item;
										});
							} else {
								return item;
							}
						}).then((item) => {
							return item ? new FlyerItemDetails(item) : null;
						});
		}

		return this._itemCache[id];
	}

	getFlyerDetails(flyerId: number): Promise<FlyerDetails> {
		if (this.config.aidHero) {
			return this.getFullFlyer(flyerId);
		}

		return this.getFlyers()
				.then((flyers) => {
					const flyer = array.find(flyers, (flyer) => flyer.id === flyerId);
					if (!flyer) return;
					if (!flyer._detailsPromise) {
						flyer._detailsPromise =
								this.backflipp.getFlyer(flyer.id.toString(), this.locationService.postalCode)
									.then((itemJson) => {
										itemJson.flyer = flyer;
										flyer.details = new FlyerDetails(itemJson);
										return flyer.details;
									});
					}

					return flyer._detailsPromise;
				});
	}

	getFullFlyer(flyerId: number, fastFlyer: boolean = true): Promise<FlyerDetails> {
		if (flyerId === null || typeof flyerId === "undefined") {
			return Promise.resolve(null);
		}

		if (!this._flyerDetailsCache[flyerId]) {
			this._flyerDetailsCache[flyerId] = this
				.backflipp
				.getFlyer(flyerId.toString(), this.locationService.postalCode, fastFlyer)
				.then((itemJson) => {
					if (itemJson) {
						return this
							.getFlyer(flyerId, false)
							.then(flyer => {
								// hack to override analytics payload from /data instead of /flyer
								if (flyer.analyticsPayload && itemJson.flyer) {
									itemJson.flyer.analytics_payload = flyer.analyticsPayload;
								}
								return new FlyerDetails(itemJson);

							});
					}
					return null;
				});
		}
		return this._flyerDetailsCache[flyerId];
	}

	async getFlyersByMerchantId(id: number): Promise<Flyer[]> {
		const flyers = await this.getFlyers();
		return flyers.filter(flyer => flyer.merchantId.toString() === id.toString());
	}

	async getFlyersByMerchantName(merchant: string): Promise<Flyer[]> {
		const flyers = await this.getFlyers();
		return flyers.filter(flyer => {
			const flyerMerchantName = slugify(flyer.merchant);
			return flyerMerchantName === slugify(merchant) ||
				flyerMerchantName === merchant ||
				merchant.includes(flyerMerchantName) ||
				merchant.includes(slugify(flyerMerchantName));
		});
	}

	getFlyers(postalCode: string = this.locationService.postalCode) {
		return this._getFlyers(postalCode);
	}

	getCategories(): Promise<Category[]> | Category[] {
		if (this._categoryResultsCache) {
			return this._categoryResultsCache;
		}

		const postalCode = this.locationService.postalCode;
		return Promise.all<any>([
			this.getFlyersDataJSON(postalCode),
			this._getFlyers(postalCode)
		]).then((values) => {
			const dataJSON: DataJSON = (<DataJSON> values[0]);
			const flyers: Flyer[] = values[1];

			this._categoryResultsCache = dataJSON.category_sort_csv.split(",").
					map((category: string) => {
						const result = new FlyerCategory(category);
						for (const flyer of flyers)
							if (flyer.categories.indexOf(category) >= 0)
								++result.count;

						return result;
					}).filter((category) => {
						// Check to see if there are flyers in this category AND that we have
						// property translations. Because we need to set a route for each catetory, we
						// need to have valid translations so we can properly construct the route. If it's
						// not available, silently ignore the category. This will allow the backend to add
						// more categories over time without us having to add support for them at the same
						// time
						return i18n(category.name) !== "" && category.count > 0;
					});
			return this._categoryResultsCache;
		});
	}

	_getFlyers(postalCode: string): Promise<Flyer[]> {
		if (!this._flyersCache[postalCode]) {
			this._flyersCache[postalCode] = this.getFlyersDataJSON(postalCode)
				.then((dataJSON) => {
					const flyers = dataJSON.flyers
						.map((json) => new Flyer(json))
						.sort((lhs, rhs) => {
							if (lhs.premium && !rhs.premium)
								return -1;
							if (!lhs.premium && rhs.premium)
								return 1;
							if (lhs.priority < rhs.priority)
								return -1;
							if (lhs.priority > rhs.priority)
								return 1;

							return rhs.availableFrom.getTime() - lhs.availableFrom.getTime();
						});
					return flyers;
				})
				.then((flyers) => {
					const filtered = flyers.filter((flyer) =>
						!this.blacklist.isMerchantBlacklisted(flyer.merchantId));
					return filtered;
				});
		}
		return this._flyersCache[postalCode];
	}

	getFlyersDataJSON(postalCode: string): Promise<DataJSON> {
		if (postalCode === null || typeof postalCode === "undefined") {
			this._flyersDataJSONCache[postalCode] = Promise.resolve(null);
		}

		if (!this._flyersDataJSONCache[postalCode]) {
			this._flyersDataJSONCache[postalCode] = this.backflipp.getFlyersData(postalCode);
		}
		return this._flyersDataJSONCache[postalCode];
	}

	getPriorityFlyer(flyers: Flyer[], flyerId: Number, flyerRunId: Number, merchantId: Number) {
		// If more than one parameter passed to page, level of priority is as follows
		// 1. Flyer ID
		// 2. Flyer Run ID
		// 3. Merchant ID (most premium if more than 1)
		return flyers.filter((flyer) =>
			flyer.id === flyerId ||
			flyer.merchantId === merchantId ||
			flyer.flyerRunId === flyerRunId
		).sort((lhs: Flyer, rhs: Flyer) => {
			if (lhs.id === flyerId)
				return -1;
			if (rhs.id === flyerId)
				return 1;
			if (lhs.flyerRunId === flyerRunId)
				return -1;
			if (rhs.flyerRunId === flyerRunId)
				return 1;
			if (lhs.merchantId === merchantId || rhs.merchantId === merchantId) {
				// When comparing flyers with same merchant ID, sort by premium and priority
				if (lhs.merchantId === rhs.merchantId) {
					if (lhs.premium && !rhs.premium)
						return -1;
					if (!lhs.premium && rhs.premium)
						return 1;
					if (lhs.priority < rhs.priority)
						return -1;
					if (lhs.priority > rhs.priority)
						return 1;
				}
				if (lhs.merchantId === merchantId)
					return -1;
				if (rhs.merchantId === merchantId)
					return -1;
			}
			return 1;
		});
	}
}
